// Sound for Tiny Town: every effect is synthesized with WebAudio (no files to download), and the town music is
// a small sequencer playing a cozy loop. Built for crowds:
//   - at most MAX_VOICES effects at once; the quietest new sound is dropped when full
//   - each kind of sound from other players is throttled, so 200 people dash-spamming is a soft patter, not noise
//   - other players' sounds fade with distance and pan left/right
// Phones only allow sound after a tap or key press, so the engine starts on the first one. Everything pauses
// while the tab is hidden.

export type Sfx = 'dash' | 'attack' | 'loot' | 'level' | 'click' | 'open' | 'close' | 'invite' | 'join' | 'ping' | 'error' | 'blip' | 'whisper' | 'shutter' | 'hit' | 'hurt' | 'poof' | 'dodge' | 'jump' | 'mount' | 'battle' | 'chain'
  | 'fire' | 'ice' | 'thunder' | 'holy' | 'heal' | 'buff' | 'debuff' | 'steal' | 'burst' | 'poison' | 'cast';
export interface Volumes { master: number; music: number; sfx: number; ui: number; others: boolean }

const MAX_VOICES = 14;
/** Minimum gap between two of the same sound coming from other players (ms). */
const OTHERS_GAP: Partial<Record<Sfx, number>> = { dash: 70, attack: 90, blip: 40, hit: 60, poof: 80 };

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master!: GainNode; private musicGain!: GainNode; private sfxGain!: GainNode; private uiGain!: GainNode;
  private musicTone!: BiquadFilterNode; private rainGain: GainNode | null = null; private cricketT = 0;
  private amb = { rain: 0, night: false };
  private noise: AudioBuffer | null = null;
  private voices = 0;
  private lastOthers = new Map<Sfx, number>();
  private music: Music | null = null;
  vol: Volumes = { master: 0.8, music: 0.5, sfx: 0.8, ui: 0.6, others: true };
  /** Effects playing right now (shown in the developer panel). */
  get playing(): number { return this.voices; }
  dropped = 0;

  constructor() {
    const unlock = () => { this.start(); };
    addEventListener('pointerdown', unlock, { capture: true });
    addEventListener('keydown', unlock, { capture: true });
    document.addEventListener('visibilitychange', () => {
      if (!this.ctx) return;
      if (document.hidden) void this.ctx.suspend(); else void this.ctx.resume();
    });
  }

  /** Create or resume the audio context. Safe to call often; only works inside a tap or key press the first time. */
  start(): void {
    if (this.ctx) { if (this.ctx.state === 'suspended' && !document.hidden) void this.ctx.resume(); return; }
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC({ latencyHint: 'interactive' });
    this.ctx = ctx;
    this.master = ctx.createGain(); this.master.connect(ctx.destination);
    // a gentle limiter, so many sounds at once never clip
    const comp = ctx.createDynamicsCompressor(); comp.threshold.value = -14; comp.ratio.value = 6; comp.attack.value = 0.003; comp.release.value = 0.15;
    comp.connect(this.master);
    this.musicTone = ctx.createBiquadFilter(); this.musicTone.type = 'lowpass'; this.musicTone.frequency.value = 18000; this.musicTone.connect(comp);
    this.musicGain = ctx.createGain(); this.musicGain.connect(this.musicTone);
    this.sfxGain = ctx.createGain(); this.sfxGain.connect(comp);
    this.uiGain = ctx.createGain(); this.uiGain.connect(comp);
    const n = ctx.createBuffer(1, ctx.sampleRate * 0.5, ctx.sampleRate), d = n.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    this.noise = n;
    this.applyVolumes();
    this.music = new Music(ctx, this.musicGain);
    if (this.wantMusic) this.music.play();
  }

  setVolumes(v: Volumes): void { this.vol = { ...v }; this.applyVolumes(); }
  private applyVolumes(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime, set = (g: GainNode, v: number) => g.gain.setTargetAtTime(v * v, t, 0.03);   // squared: sliders feel even
    set(this.master, this.vol.master); set(this.musicGain, this.vol.music * 0.55); set(this.sfxGain, this.vol.sfx); set(this.uiGain, this.vol.ui);
  }

  private wantMusic = false;
  playMusic(on: boolean): void { this.wantMusic = on; if (!this.music) return; if (on) this.music.play(); else this.music.stop(); }

  /** Weather and time of day: a rain bed, crickets at night, and softer music after dark or in the rain. */
  setAmbience(rain: number, night: boolean): void {
    this.amb = { rain, night };
    const ctx = this.ctx; if (!ctx || ctx.state !== 'running') return;
    const t = ctx.currentTime;
    this.musicTone.frequency.setTargetAtTime(night || rain > 0.3 ? 1400 : 18000, t, 1.5);
    if (rain > 0.01 && !this.rainGain) {
      const src = ctx.createBufferSource(), f = ctx.createBiquadFilter(), f2 = ctx.createBiquadFilter(), g = ctx.createGain();
      src.buffer = this.noise; src.loop = true; f.type = 'lowpass'; f.frequency.value = 2200; f2.type = 'highpass'; f2.frequency.value = 300; g.gain.value = 0;
      src.connect(f); f.connect(f2); f2.connect(g); g.connect(this.sfxGain); src.start();
      this.rainGain = g;
    }
    this.rainGain?.gain.setTargetAtTime(rain * 0.16, t, 1);
    const now = performance.now();
    if (night && rain < 0.3 && now > this.cricketT) {
      this.cricketT = now + 300 + Math.random() * 1600;
      const n = 2 + Math.floor(Math.random() * 3), f = 4200 + Math.random() * 900, pan = ctx.createStereoPanner(); pan.pan.value = Math.random() * 1.6 - 0.8; pan.connect(this.sfxGain);
      for (let i = 0; i < n; i++) tone(ctx, pan, 'sine', f, f, t + i * 0.07, 0.04, 0.012, 0.004);
      setTimeout(() => pan.disconnect(), 1000);
    }
  }

  /** A roll of thunder, a moment after the flash. */
  thunder(): void {
    const ctx = this.ctx; if (!ctx || ctx.state !== 'running' || !this.noise) return;
    const t = ctx.currentTime, s = ctx.createBufferSource(), f = ctx.createBiquadFilter(), g = ctx.createGain();
    s.buffer = this.noise; s.loop = true; f.type = 'lowpass'; f.frequency.setValueAtTime(320, t); f.frequency.exponentialRampToValueAtTime(90, t + 2.6);
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.5, t + 0.08); g.gain.exponentialRampToValueAtTime(0.0001, t + 2.8);
    s.connect(f); f.connect(g); g.connect(this.sfxGain); s.start(t); s.stop(t + 3);
  }

  /** Play an effect. `at` is the source's offset from the listener in pixels, for other players' sounds. */
  play(s: Sfx, at?: { dx: number; dy: number }, pitch = 1): void {
    const ctx = this.ctx; if (!ctx || ctx.state !== 'running') return;
    let gain = 1, pan = 0;
    if (at) {
      if (!this.vol.others) return;
      const gap = OTHERS_GAP[s] ?? 60, now = performance.now();
      if (now - (this.lastOthers.get(s) ?? 0) < gap) { this.dropped++; return; }
      this.lastOthers.set(s, now);
      const d = Math.hypot(at.dx, at.dy); if (d > 220) return;
      gain = 0.55 * Math.max(0, 1 - d / 220); pan = Math.max(-0.8, Math.min(0.8, at.dx / 160));
      if (gain < 0.04) return;
    }
    if (this.voices >= MAX_VOICES && at) { this.dropped++; return; }   // full: others' sounds give way to yours
    const ui = s === 'click' || s === 'open' || s === 'close' || s === 'blip';
    const out = ctx.createGain(); out.gain.value = gain;
    let node: AudioNode = out;
    if (pan) { const p = ctx.createStereoPanner(); p.pan.value = pan; out.connect(p); node = p; }
    node.connect(ui ? this.uiGain : this.sfxGain);
    const len = RECIPES[s](ctx, out, this.noise!, ctx.currentTime + 0.005, pitch);
    this.voices++;
    setTimeout(() => { this.voices--; try { node.disconnect(); out.disconnect(); } catch { /* gone */ } }, (len + 0.1) * 1000);
  }
}

// ---------- effect recipes: build the sound into `out`, return its length in seconds ----------
type Recipe = (c: BaseAudioContext, out: AudioNode, noise: AudioBuffer, t: number, pitch: number) => number;

function tone(c: BaseAudioContext, out: AudioNode, type: OscillatorType, f0: number, f1: number, t: number, dur: number, vol: number, attack = 0.005): void {
  const o = c.createOscillator(), g = c.createGain();
  o.type = type; o.frequency.setValueAtTime(f0, t); if (f1 !== f0) o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
  g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(vol, t + attack); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g); g.connect(out); o.start(t); o.stop(t + dur + 0.02);
}
function hiss(c: BaseAudioContext, out: AudioNode, noise: AudioBuffer, t: number, dur: number, vol: number, f0: number, f1: number, q = 1.2): void {
  const s = c.createBufferSource(), f = c.createBiquadFilter(), g = c.createGain();
  s.buffer = noise; f.type = 'bandpass'; f.Q.value = q; f.frequency.setValueAtTime(f0, t); f.frequency.exponentialRampToValueAtTime(f1, t + dur);
  g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(vol, t + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  s.connect(f); f.connect(g); g.connect(out); s.start(t, Math.random() * 0.3); s.stop(t + dur + 0.02);
}
const note = (n: number) => 440 * Math.pow(2, (n - 69) / 12);

const RECIPES: Record<Sfx, Recipe> = {
  dash: (c, o, n, t, p) => { hiss(c, o, n, t, 0.16, 0.35, 900 * p, 3200 * p, 0.9); tone(c, o, 'triangle', 300 * p, 520 * p, t, 0.08, 0.06); return 0.18; },
  attack: (c, o, n, t, p) => { hiss(c, o, n, t, 0.12, 0.3, 2600 * p, 700 * p, 1.6); tone(c, o, 'square', 180 * p, 90 * p, t + 0.02, 0.07, 0.05); return 0.15; },
  loot: (c, o, _n, t) => { tone(c, o, 'square', note(76), note(76), t, 0.08, 0.08); tone(c, o, 'square', note(83), note(83), t + 0.07, 0.14, 0.08); return 0.24; },
  level: (c, o, _n, t) => { [72, 76, 79, 84].forEach((m, i) => tone(c, o, 'square', note(m), note(m), t + i * 0.09, i === 3 ? 0.45 : 0.12, 0.08)); tone(c, o, 'triangle', note(48), note(48), t + 0.27, 0.5, 0.12); return 0.8; },
  click: (c, o, _n, t) => { tone(c, o, 'square', 1250, 900, t, 0.03, 0.04); return 0.05; },
  open: (c, o, _n, t) => { tone(c, o, 'triangle', note(72), note(79), t, 0.09, 0.1); return 0.1; },
  close: (c, o, _n, t) => { tone(c, o, 'triangle', note(79), note(72), t, 0.08, 0.08); return 0.1; },
  invite: (c, o, _n, t) => { [84, 88, 91].forEach((m, i) => tone(c, o, 'sine', note(m), note(m), t + i * 0.11, 0.5, 0.12, 0.002)); return 0.8; },
  join: (c, o, _n, t) => { [67, 72, 76].forEach((m, i) => tone(c, o, 'triangle', note(m), note(m), t + i * 0.07, 0.22, 0.12)); return 0.4; },
  ping: (c, o, _n, t) => { tone(c, o, 'sine', note(86), note(86), t, 0.18, 0.07, 0.002); return 0.2; },
  whisper: (c, o, _n, t) => { tone(c, o, 'sine', note(81), note(81), t, 0.12, 0.06, 0.002); tone(c, o, 'sine', note(88), note(88), t + 0.08, 0.2, 0.06, 0.002); return 0.3; },
  error: (c, o, _n, t) => { tone(c, o, 'square', 140, 110, t, 0.16, 0.06); return 0.18; },
  shutter: (c, o, n, t) => { hiss(c, o, n, t, 0.05, 0.4, 4000, 2500, 0.8); hiss(c, o, n, t + 0.07, 0.06, 0.3, 3000, 1800, 0.8); tone(c, o, 'square', 1800, 1200, t, 0.02, 0.04); return 0.15; },
  // combat: soft and bouncy rather than crunchy
  hit: (c, o, n, t, p) => { tone(c, o, 'triangle', 420 * p, 210 * p, t, 0.08, 0.12); hiss(c, o, n, t, 0.05, 0.15, 1800 * p, 900 * p, 1.2); return 0.1; },
  hurt: (c, o, n, t) => { tone(c, o, 'square', 300, 160, t, 0.12, 0.07); hiss(c, o, n, t, 0.08, 0.2, 1200, 500, 1); return 0.14; },
  poof: (c, o, n, t, p) => { hiss(c, o, n, t, 0.25, 0.25, 600 * p, 3000 * p, 0.7); [79, 84].forEach((m, i) => tone(c, o, 'sine', note(m) * p, note(m) * p, t + 0.05 + i * 0.06, 0.12, 0.06, 0.002)); return 0.3; },
  dodge: (c, o, n, t) => { hiss(c, o, n, t, 0.1, 0.2, 3000, 5000, 1); tone(c, o, 'sine', note(91), note(96), t, 0.08, 0.05, 0.002); return 0.12; },
  // the battle intro: a falling whoosh, then a bright little fanfare climbing up (SNES style)
  battle: (c, o, n, t) => {
    hiss(c, o, n, t, 0.5, 0.35, 5000, 400, 0.6);
    tone(c, o, 'sawtooth', 880, 110, t, 0.45, 0.05);
    [64, 67, 71, 76, 79, 83, 88].forEach((m, i) => tone(c, o, 'square', note(m), note(m), t + 0.42 + i * 0.045, 0.09, 0.07));
    tone(c, o, 'triangle', note(40), note(40), t + 0.42, 0.5, 0.16);
    tone(c, o, 'square', note(88), note(88), t + 0.74, 0.35, 0.06);
    return 1.2;
  },
  chain: (c, o, _n, t, p) => { [72, 76, 79, 84].forEach((m, i) => tone(c, o, 'square', note(m) * p, note(m) * p, t + i * 0.035, 0.08, 0.06)); return 0.25; },
  jump: (c, o, _n, t, p) => { tone(c, o, 'square', 330 * p, 660 * p, t, 0.09, 0.05); return 0.1; },
  mount: (c, o, n, t) => { hiss(c, o, n, t, 0.2, 0.18, 800, 2400, 0.7); [67, 74, 79].forEach((m, i) => tone(c, o, 'triangle', note(m), note(m), t + i * 0.05, 0.14, 0.08)); return 0.3; },
  blip: (c, o, _n, t, p) => { tone(c, o, 'square', 520 * p, 520 * p, t, 0.035, 0.035, 0.002); return 0.05; },
  // spells: each element has its own voice
  cast: (c, o, _n, t) => { [79, 84, 88, 91].forEach((m, i) => tone(c, o, 'sine', note(m), note(m + 12), t + i * 0.04, 0.12, 0.04, 0.002)); return 0.3; },
  fire: (c, o, n, t) => { hiss(c, o, n, t, 0.5, 0.4, 300, 1800, 0.6); hiss(c, o, n, t + 0.12, 0.35, 0.25, 1200, 400, 0.8); tone(c, o, 'sawtooth', 120, 60, t, 0.35, 0.05); return 0.55; },
  ice: (c, o, n, t) => { [96, 91, 98, 93, 100].forEach((m, i) => tone(c, o, 'triangle', note(m), note(m), t + i * 0.05, 0.18, 0.05, 0.002)); hiss(c, o, n, t, 0.3, 0.15, 6000, 3000, 2); return 0.45; },
  thunder: (c, o, n, t) => { hiss(c, o, n, t, 0.06, 0.6, 4000, 2000, 0.5); tone(c, o, 'square', 90, 45, t + 0.03, 0.4, 0.12); hiss(c, o, n, t + 0.08, 0.5, 0.3, 300, 120, 0.4); return 0.6; },
  holy: (c, o, _n, t) => { [72, 79, 84, 88, 91].forEach((m, i) => tone(c, o, 'sine', note(m), note(m), t + i * 0.06, 0.5, 0.05, 0.01)); tone(c, o, 'triangle', note(96), note(96), t + 0.3, 0.5, 0.04); return 0.8; },
  heal: (c, o, _n, t) => { [76, 79, 83, 88].forEach((m, i) => tone(c, o, 'sine', note(m), note(m), t + i * 0.07, 0.35, 0.06, 0.01)); return 0.6; },
  buff: (c, o, _n, t) => { tone(c, o, 'triangle', note(67), note(79), t, 0.25, 0.08); tone(c, o, 'sine', note(84), note(84), t + 0.18, 0.25, 0.05); return 0.45; },
  debuff: (c, o, _n, t) => { tone(c, o, 'triangle', note(72), note(60), t, 0.3, 0.08); tone(c, o, 'sine', note(66), note(54), t + 0.12, 0.3, 0.05); return 0.45; },
  poison: (c, o, _n, t) => { [0, 1, 2].forEach(i => tone(c, o, 'sine', 300 + i * 70, 500 + i * 90, t + i * 0.08, 0.07, 0.05, 0.002)); return 0.3; },
  steal: (c, o, n, t) => { hiss(c, o, n, t, 0.08, 0.2, 3500, 6000, 1.2); tone(c, o, 'square', note(88), note(93), t + 0.06, 0.08, 0.05); return 0.18; },
  burst: (c, o, n, t) => { hiss(c, o, n, t, 0.4, 0.4, 600, 5000, 0.5); [60, 67, 72, 79, 84].forEach((m, i) => tone(c, o, 'square', note(m), note(m), t + 0.05 + i * 0.03, 0.3, 0.06)); tone(c, o, 'sawtooth', 220, 55, t, 0.5, 0.06); return 0.6; },
};

// ---------- music ----------
/** A cozy looping town tune: 8 bars, chords, a walking bass, a soft arpeggio and a little melody. */
export class Music {
  private timer = 0; private step = 0; private next = 0; private on = false;
  private readonly bpm = 88;
  // I - vi - IV - V - I - iii - IV - V in C (MIDI roots) with chord tones
  private readonly chords = [[60, 64, 67], [57, 60, 64], [53, 57, 60], [55, 59, 62], [60, 64, 67], [52, 55, 59], [53, 57, 60], [55, 59, 62]];
  private readonly melody = [76, -1, 74, 72, 74, -1, 76, 79, 72, -1, -1, 69, 72, 74, 72, -1, 69, -1, 72, 74, 76, -1, 74, 72, 74, -1, -1, 71, 74, -1, 79, -1,
    76, -1, 79, 81, 79, -1, 76, 74, 76, -1, 72, -1, 71, 72, 74, -1, 72, -1, 69, 72, 69, -1, 67, 69, 67, -1, 71, 74, 72, -1, -1, -1];
  private shaker: AudioBuffer;
  constructor(private c: BaseAudioContext, private out: AudioNode) {
    const len = Math.floor(c.sampleRate * 0.03), b = c.createBuffer(1, len, c.sampleRate), d = b.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
    this.shaker = b;
  }

  play(): void {
    if (this.on || !(this.c instanceof AudioContext)) return;
    const c = this.c;
    this.on = true; this.next = c.currentTime + 0.1;
    const look = () => {
      // schedule a little ahead of time so timer jitter never makes the music stutter
      while (this.next < c.currentTime + 0.25) { this.schedule(this.step, this.next); this.next += 60 / this.bpm / 2; this.step = (this.step + 1) % 64; }
    };
    look(); this.timer = window.setInterval(look, 60);
  }
  stop(): void { this.on = false; clearInterval(this.timer); }

  schedule(i: number, t: number): void {
    const bar = Math.floor(i / 8), beat = i % 8, ch = this.chords[bar], c = this.c, o = this.out;
    const n = (m: number) => 440 * Math.pow(2, (m - 69) / 12);
    if (beat % 4 === 0) tone(c, o, 'triangle', n(ch[0] - 24), n(ch[0] - 24), t, 0.6, 0.16, 0.01);         // bass on 1 and 3
    if (beat % 4 === 2) tone(c, o, 'triangle', n(ch[2] - 24), n(ch[2] - 24), t, 0.3, 0.1, 0.01);
    tone(c, o, 'sine', n(ch[beat % 3] + 12), n(ch[beat % 3] + 12), t, 0.28, 0.035, 0.01);                // arpeggio
    if (beat === 0) for (const m of ch) tone(c, o, 'triangle', n(m), n(m), t, 1.6, 0.022, 0.25);           // soft pad
    const m = this.melody[i];
    if (m > 0) tone(c, o, 'square', n(m), n(m), t, 0.32, 0.028, 0.01);
    if (beat % 2 === 1) { const sh = c.createBufferSource(), f = c.createBiquadFilter(), g = c.createGain(); sh.buffer = this.shaker; f.type = 'highpass'; f.frequency.value = 6000; g.gain.value = 0.025; sh.connect(f); f.connect(g); g.connect(o); sh.start(t); }   // a tiny shaker on the off-beats
  }
}

/** Renders the music and every effect to a buffer without playing it (for listening tests and tools). */
export async function renderPreview(seconds = 24): Promise<AudioBuffer> {
  const rate = 44100, c = new OfflineAudioContext(2, rate * (seconds + 6), rate), out = c.createGain(); out.gain.value = 0.6; out.connect(c.destination);
  const m = new Music(c, out), step = 60 / 88 / 2;
  for (let i = 0, t = 0.1; t < seconds; i++, t += step) m.schedule(i % 64, t);
  const n = c.createBuffer(1, rate / 2, rate), d = n.getChannelData(0); for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  (Object.keys(RECIPES) as Sfx[]).forEach((k, i) => RECIPES[k](c, out, n, seconds + 0.1 + i * 0.42, 1));
  return c.startRendering();
}
